import { NGXLogger } from 'ngx-logger';
import { ChangeDetectorRef, Component, OnInit, OnChanges, SimpleChanges, Input, ViewChild, Output, EventEmitter, OnDestroy, ElementRef, Inject, DOCUMENT } from '@angular/core';
import { LocalStorageService } from 'ngx-webstorage';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { sortBy, cloneDeep, map } from 'lodash';
import { SelectEvent, TabStripComponent } from '@progress/kendo-angular-layout';
import { MultiColumnComboBoxComponent, PreventableEvent } from '@progress/kendo-angular-dropdowns';
import { IntlService } from '@progress/kendo-angular-intl';
import { State } from '@progress/kendo-data-query';
import { Subscription } from 'rxjs';
import { MemberListService } from 'src/app/core/service/grid-data/grid-data-member.service';
import { MemberService } from 'src/app/core/service/member.service';
import { MessengerService } from 'src/app/core/service/messenger.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { FileRestrictions, UploadEvent } from '@progress/kendo-angular-upload';
import { CustomFile } from 'src/app/message/model/custom-file';
import { SettingService } from 'src/app/core/service/setting.service';

import { caretAltDownIcon } from "@progress/kendo-svg-icons";
import { AssessmentService } from 'src/app/core/service/assessment.service';
import { Base64 } from 'js-base64';
import copyToClipboard from 'copy-to-clipboard';
import
{
  clearNgxJsonViewerPathSelection,
  highlightNgxJsonViewerPathSegment,
  resolveNgxJsonViewerPath
} from 'src/app/core/utility/ngx-json-viewer-path.util';

@Component({
  standalone: false,
  selector: 'app-formly-designer',
  templateUrl: './formly-designer.component.html',
  styleUrls: ['./formly-designer.component.scss']
})
export class FormlyDesignerComponent implements OnInit, OnChanges, OnDestroy
{
  private static readonly CONTEXT_MEMBER_STORAGE_KEY = 'formly-designer-context-member-id';
  private static readonly PREVIEW_TAB_INDEX = 2;

  @ViewChild('tabstripFlag') public tabstrip: TabStripComponent;
  @ViewChild('patientModelViewer') public patientModelViewer: ElementRef<HTMLElement>;
  @ViewChild('patientSearchCombo') public patientSearchCombo: MultiColumnComboBoxComponent;

  @Input() public assessmentId: number;
  @Input() public model = {};
  @Input() public fields: FormlyFieldConfig[];
  @Input() public fieldsPreview: FormlyFieldConfig[];
  @Input() public flags: any;
  @Input() public tasks: any;
  @Input() public serviceTypes: any;
  @Input() public channels: any;
  @Input() public needs: any;
  @Input() public isDeleted = false;
  @Input() public isLocked = false;
  @Input() public consentTypeAssessment = false;

  @Output() public fieldsChanged: EventEmitter<FormlyFieldConfig[]> = new EventEmitter();
  @Output() public flagsChanged: EventEmitter<any[]> = new EventEmitter();
  @Output() public tasksChanged: EventEmitter<any[]> = new EventEmitter();
  @Output() public serviceTypesChanged: EventEmitter<any[]> = new EventEmitter();
  @Output() public channelsChanged: EventEmitter<any[]> = new EventEmitter();
  @Output() public needsChanged: EventEmitter<any[]> = new EventEmitter();

  public showModel = false;
  public isNew = false;
  public isCopy = false;
  public loading = false;
  public fieldEditModel: FormlyFieldConfig;
  public usedKeys = [];
  public dragStarted = false;
  public isMouseOverActionButtons = false;
  public form = new UntypedFormGroup({});
  public communityEnabled = false;
  public icons = { caretDown: caretAltDownIcon };
  public forceResetPreview = false;
  public foundFieldInArray = false;
  public patientModel: Record<string, unknown> | null = null;
  /** Same structure as patientModel; photo strings shortened for ngx-json-viewer display only. */
  public patientModelForViewer: Record<string, unknown> | null = null;
  public formlyPreviewOptions: FormlyFormOptions = { formState: { patient: null } };
  public selectedModelPath: string | null = null;
  public patientSearchItems: Array<any> = [];
  public selectedPatientSearch: any = null;
  public patientSearchOperator: 'startsWith' | 'contains' = 'startsWith';
  public loadingPatientSearch = false;
  public loadingPatientModel = false;
  public patientLabel: Record<string, string> = {};

  public listControlTypes: Array<{ text: string; value: string; icon: string }> = [
    { text: 'Text', value: 'input', icon: 'fa-text' },
    { text: 'Text (Multiline)', value: 'textarea', icon: 'fa-text' },
    { text: 'Number', value: 'number', icon: 'fa-pen-square' },
    { text: 'Email', value: 'email', icon: 'fa-at' },
    { text: 'Select', value: 'select', icon: 'fa-caret-square-down' },
    { text: 'Radio', value: 'radio', icon: 'fa-list-alt' },
    { text: 'Checkbox', value: 'checkbox', icon: 'fa-check-square' },
    { text: 'Horizontal Line', value: 'hr', icon: 'fa-horizontal-rule' },
    { text: 'Header', value: 'header', icon: 'fa-heading' },
    { text: 'Checkbox (Multiple)', value: 'multicheckbox', icon: 'fa-ballot-check' },
    { text: 'Date', value: 'date', icon: 'fa-calendar-edit' },
    { text: 'Tab', value: 'tabs', icon: 'fa-layer-group' },
    { text: 'Label', value: 'label', icon: 'fa-code' },
    { text: 'Date Time', value: 'datetime', icon: 'fa-calendar-edit' },
    { text: 'Select (Multiple)', value: 'multiselect', icon: 'fa-caret-square-down' },
  ];

  // Import restrictions
  public fileRestrictions: FileRestrictions =
    {
      allowedExtensions: ['.json'],
      maxFileSize: 2000
    };
  public invalidFileExtensionText: string;

  private contextPatientRestoreStarted = false;
  private messagingTabSubscription: Subscription;
  private readonly patientSearchMinLength = 3;

  constructor(
    private translate: TranslateService,
    private logger: NGXLogger,
    private messengerService: MessengerService,
    private messageHandlerService: MessageHandlerService,
    private elem: ElementRef,
    private settingService: SettingService,
    private assessmentService: AssessmentService,
    private memberListService: MemberListService,
    private memberService: MemberService,
    private storageService: LocalStorageService,
    private changeDetectorRef: ChangeDetectorRef,
    public intl: IntlService,
    @Inject(DOCUMENT) private document: Document)
  {
    this.messagingTabSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message && message.type)
        {
          if (message.type === 'selectFormlyDesignerTabChannel')
          {
            this.tabstrip.selectTab(3);
          }
          else if (message.type === 'selectFormlyDesignerTabFlag')
          {
            this.tabstrip.selectTab(4);
          }
          else if (message.type === 'selectFormlyDesignerTabNeed')
          {
            this.tabstrip.selectTab(5);
          }
          else if (message.type === 'selectFormlyDesignerTabServiceType')
          {
            this.tabstrip.selectTab(6);
          }
          else if (message.type === 'selectFormlyDesignerTabTask')
          {
            this.tabstrip.selectTab(7);
          }
        }
      }
    });
  }

  public get patientContextSearchEnabled(): boolean
  {
    return !!(this.fields?.length) && !this.isDeleted && !this.isLocked;
  }

  public ngOnInit(): void
  {
    this.syncPreviewPatientFormState();
    this.forceResetPreview = false;
    this.settingService.getCachedSessionSettings().subscribe({
      next: (sessionSetting) =>
      {
        this.communityEnabled = sessionSetting.communityEnabled;
        this.patientLabel = { patientLabel: sessionSetting.patientLabel };
      }
    });

    // Get the translation used when file types is invalid
    this.translate.get(['INVALID-FILE-TYPE', 'FORM-CONTROL-TYPES']).subscribe({
      next: (res) =>
      {
        this.invalidFileExtensionText = res['INVALID-FILE-TYPE'];

        const splitted = res['FORM-CONTROL-TYPES'].split(',');
        for (let i = 0; i < splitted.length; i++)
        {
          if (this.listControlTypes.length > i)
          {
            this.listControlTypes[i].text = splitted[i];
          }
        }
        this.listControlTypes = sortBy(this.listControlTypes, ['text']);
      }
    });

    this.tryRestoreStoredContextPatient();
  }

  public ngOnChanges(changes: SimpleChanges): void
  {
    if (changes.fields)
    {
      this.tryRestoreStoredContextPatient();
    }
  }

  public changePatientSearchOperator(operator: 'startsWith' | 'contains'): void
  {
    this.patientSearchOperator = operator;
  }

  public handlePatientSearchOpen(event: PreventableEvent): void
  {
    if (this.patientSearchCombo?.text?.length < this.patientSearchMinLength)
    {
      event.preventDefault();
    }
  }

  public handlePatientSearchFilterChange(searchTerm: string): void
  {
    if (!searchTerm || searchTerm.length < this.patientSearchMinLength)
    {
      this.patientSearchItems = [];
      return;
    }

    this.loadingPatientSearch = true;
    const subjectState: State = { skip: 0, take: 50, filter: { logic: 'and', filters: [] } };
    subjectState.filter.filters.push({
      field: 'fullName',
      operator: this.patientSearchOperator.toLocaleLowerCase(),
      value: searchTerm
    });

    this.memberListService.queryByState(subjectState, (value: any) =>
    {
      this.patientSearchItems = value?.data ?? [];
      this.loadingPatientSearch = false;
      this.changeDetectorRef.markForCheck();
    });
  }

  public onPatientSearchValueChange(selection: any): void
  {
    this.selectedPatientSearch = selection ?? null;
    if (!selection?.id)
    {
      this.applyPatientContext(null);
      return;
    }

    this.loadingPatientModel = true;
    this.memberService.getMember(selection.id).subscribe({
      next: (member) =>
      {
        this.applyPatientContext(member);
        this.loadingPatientModel = false;
        this.changeDetectorRef.markForCheck();
      },
      error: (error) =>
      {
        this.loadingPatientModel = false;
        this.messageHandlerService.displayError(error);
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  public ngOnDestroy(): void
  {
    if (this.messagingTabSubscription !== undefined) { this.messagingTabSubscription.unsubscribe(); }
  }

  /**
   * Reset form used for testing form design state.
   */
  public reset(): void
  {
    this.loading = true;
    this.forceResetPreview = false;
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this.model = {};
    if (this.fieldsPreview?.length)
    {
      this.preparePreviewForm();
    }
    else
    {
      this.form.reset();
      this.assessmentService.setFormlyModelDefaults(this.fields, this.model);
      this.assessmentService.setFormlyControlsSum(this.fieldsPreview, this.form);
    }
    setTimeout(() => { this.loading = false; });
  }

  /**
   * Event called when adding new control from the drop down selection.
   *
   * @param event click event
   */
  public onAddControlClick(event: any): void
  {
    if (event)
    {
      let fieldConfig: FormlyFieldConfig = null;
      // Set minimum properties
      switch (event.value)
      {
        // email, number and text are all input. templateOptions dictate which input it is.
        case 'email':
        case 'number':
        case 'input':
          fieldConfig = {
            type: 'input',
            templateOptions: {
              type: (event.value === 'input' ? 'text' : event.value)
            }
          };
          break;

        case 'radio':
        case 'select':
        case 'multiselect':
        case 'checkbox':
        case 'multicheckbox':
        case 'textarea':
        case 'date':
        case 'datetime':
          fieldConfig = {
            type: event.value
          };
          fieldConfig.templateOptions = {};
          // Init options for select and radio
          if (event.value === 'select' || event.value === 'radio' || event.value === 'multicheckbox' || event.value === 'multiselect')
          {
            fieldConfig.templateOptions.options = [];
          }
          break;

        // Tab
        case 'tabs':
          fieldConfig = {
            type: event.value,
          };
          fieldConfig.fieldGroup = [];
          break;

        // Horizontal Line
        case 'hr':
          this.isNew = true;
          this.isCopy = false;
          this.onApplyEditField({ template: '<hr />' });
          this.fieldsChanged.emit(this.fields);
          break;

        // Header
        case 'header':
          fieldConfig = {
            templateOptions: {
              type: 'header',
              label: ''
            }
          };
          break;

        // Label Text
        case 'label':
          fieldConfig = {
            type: 'label',
            templateOptions: {
              type: 'label',
              description: ''
            }
          };
          break;
      }

      if (fieldConfig)
      {
        this.isNew = true;
        this.isCopy = false;
        this.setUsedKeys();
        this.fieldEditModel = fieldConfig;
        this.fieldsChanged.emit(this.fields);
      }
    }
  }

  /**
   * Event occurs when draging is over an item is released to it's position.
   *
   * @param event Event
   */
  public onDragOver(event: any, control: any): void
  {
    event.preventDefault();
    control.moveItem(event.oldIndex, event.index);
    this.fieldsChanged.emit(this.fields);
  }

  /**
   * Event occurs when start draging an item.
   *
   * @param event Event
   */
  public onDragStart(event: any): void
  {
    if (this.isMouseOverActionButtons || this.isDeleted || this.isLocked)
    {
      event.preventDefault();
      return;
    }
    this.dragStarted = true;
  }

  /**
   * Event occurs when drag is completed.
   */
  public onDragEnd(): void
  {
    this.dragStarted = false;
  }

  /**
   * Delete a field from the editable fields array.
   *
   * @param index Index to delete.
   */
  public deleteField(index: number, fields: any): void
  {
    // Validate if fields is being used within Flags, Tasks, Service Types or Channels rules before allowing deletion
    if (!this.canDeleteField(fields[index]))
    {
      return;
    }

    this.loading = true;
    fields.splice(index, 1);

    this.fieldsChanged.emit(this.fields);
    setTimeout(() =>
    {
      this.loading = false;
    });
  }

  /**
   * Edit a field using modal.
   *
   * @param item Edit field item to edit.
   */
  public editField(item: any): void
  {
    this.isNew = false;
    this.isCopy = false;
    this.setUsedKeys();
    this.fieldEditModel = item;
  }

  /**
   * Copy a field
   * @param item Edit field item to edit.
   */
  public copyField(item: any): void
  {
    this.isNew = false;
    this.isCopy = true;
    this.setUsedKeys();
    this.fieldEditModel = cloneDeep(item);
  }

  /**
   * Mouse enter event when mouse hover action buttons div. This is required to stop drag event.
   */
  public mouseEnterActionButtonsDiv(): void
  {
    this.isMouseOverActionButtons = true;
  }

  /**
   * Mouse leave event occurs when mouse blur the action buttons div.
   */
  public mouseLeaveActionButtonsDiv(): void
  {
    this.isMouseOverActionButtons = false;
  }

  /**
   * Field edit modal has been cancelled or closed. Being used to reset the fieldEditModel.
   *
   * @param e Event
   */
  public onCancelEditField(): void
  {
    this.fieldEditModel = undefined;
  }

  /**
   * Field edit modal has been closed and must apply changes.
   *
   * @param e Event
   */
  public onApplyEditField(field: FormlyFieldConfig | any): void
  {
    this.forceResetPreview = true;
    this.loading = true;
    this.fieldEditModel = undefined;

    if (field.key && !this.isCopy) 
    {
      this.foundFieldInArray = false;
      this.foundFieldAndUpdate(field.key.toString(), field, this.fields, null);

      if (!this.foundFieldInArray)
      {
        // If there is a tab, it must be added in the fieldGroup
        if (field.tab) 
        {
          this.foundFieldAndAdd(field.tab, field, this.fields)
        }
        // Tab have special update due to support templateOptions and fieldGroup from the same UI (where it can be a template or control)
        else if (field.type === 'tabs')
        {
          const fieldIndex = this.fields.findIndex(f => f.key === field.key);
          if (fieldIndex >= 0)
          {
            // Checking tab data to update
            field.templateOptions.options.forEach((o: any) =>
            {
              // Updating tab info in fieldGroup
              const foundField = this.fields[fieldIndex].fieldGroup.find(x => x.key === o.value);
              if (foundField)
              {
                foundField.templateOptions.label = o.label;
              }
              else
              {
                this.fields[fieldIndex].fieldGroup.push({ key: o.value, templateOptions: { label: o.label }, fieldGroup: [] });
              }

              // Updating templateOptions
              this.fields[fieldIndex].templateOptions = field.templateOptions;

              // Updating expression
              if (field.hideExpression)
              {
                this.fields[fieldIndex].hideExpression = field.hideExpression;
              }
              else
              {
                delete this.fields[fieldIndex].hideExpression;
              }

              if (field.expressionProperties)
              {
                this.fields[fieldIndex].expressionProperties = field.expressionProperties;
              }
              else
              {
                delete this.fields[fieldIndex].expressionProperties;
              }

              // Updating tab info in templateOptions
              let options: any = this.fields[fieldIndex].templateOptions.options;
              const foundOption = options.find((x: any) => x.value === o.value);
              if (foundOption)
              {
                foundOption.label = o.label;
              }
              else
              {
                options.push({ label: o.label, value: o.value, externalId: o.externalId });
              }

              // Deleting tabs if no longer needed for fieldGroup and templateOptions
              const currentTabs = map(field.fieldGroup, 'key');
              this.fields[fieldIndex].fieldGroup = this.fields[fieldIndex].fieldGroup.filter((x: any) => currentTabs.includes(x.key));

              options = this.fields[fieldIndex].templateOptions.options;
              this.fields[fieldIndex].templateOptions.options = options.filter((x: any) => currentTabs.includes(x.value));
            });

            // Re-order fieldgroup
            const orderedFieldGroup = [];
            this.fields[fieldIndex].templateOptions.options.forEach((o: any) =>
            {
              const field = this.fields[fieldIndex].fieldGroup.find((f: any) => f.key === o.value);
              if (field) { orderedFieldGroup.push(field); }
            });
            this.fields[fieldIndex].fieldGroup = orderedFieldGroup;
          }
          else
          {
            this.fields.push(field);
          }
        }
        else
        {
          this.fields.push(field);
        }
      }
    }
    else
    {
      if (this.isNew || this.isCopy)
      {
        this.fields.push(field);
      }
      else
      {
        // Locate the field to update
        const fieldIndex = this.fields.findIndex(f => f.key === field.key);
        if (fieldIndex >= 0)
        {
          this.fields[fieldIndex] = field;
        }
      }
    }

    this.logger.info('New Field', field);

    // Emit updated field changes to calling form.
    this.fieldsChanged.emit(this.fields);
    setTimeout(() =>
    {
      this.logger.info('All fields', this.fields);
      this.loading = false;
      // Make sure the field is into view after updates
      setTimeout(() =>
      {
        if (field && field.key)
        {
          const element = this.document.getElementById(field.key.toString());
          if (element)
          {
            element.scrollIntoView({ block: 'center', inline: 'nearest' });
          }
        }
      });
    });
  }

  /**
   * Flag edit form has been changed and must apply changes/state.
   *
   * @param e Event
   */
  public onFlagFormChanged(flagForm: any): void
  {
    this.flagsChanged.emit(flagForm);
  }

  /**
   * Task edit form has been changed and must apply changes/state.
   *
   * @param e Event
   */
  public onTaskFormChanged(taskForm: any): void
  {
    this.tasksChanged.emit(taskForm);
  }

  /**
   * Service Type edit form has been changed and must apply changes/state.
   *
   * @param e Event
   */
  public onServiceTypeFormChanged(serviceTypeForm: any): void
  {
    this.serviceTypesChanged.emit(serviceTypeForm);
  }

  /**
   * Channel form has been changed and must apply changes/state.
   *
   * @param e Event
   */
  public onChannelFormChanged(channelForm: any): void
  {
    this.channelsChanged.emit(channelForm);
  }

  /**
   * Need form has been changed and must apply changes/state.
   *
   * @param e Event
   */
  public onNeedFormChanged(needForm: any): void
  {
    this.needsChanged.emit(needForm);
  }

  /**
   * Event occurs when tab is selected.
   *
   * @param e Event
   */
  public onTabSelect(e: SelectEvent): void
  {
    // If preview tab, must clone fields to fieldsPreview to prevent altering hideExpression
    if (e.index === FormlyDesignerComponent.PREVIEW_TAB_INDEX)
    {
      this.fieldsPreview = cloneDeep(this.fields);
      this.assessmentService.runAfterFormlyFieldsAttach(() =>
      {
        if (this.forceResetPreview)
        {
          this.reset();
        }
        else
        {
          this.preparePreviewForm();
        }
      }, 2);
    }
  }

  /**
   * File has been selected.
   *
   * @param event Event
   */
  public onFileChanged(event: UploadEvent): void
  {
    event.files.forEach((file) =>
    {
      // Validate file size
      if ((file.size / 1024) > this.fileRestrictions.maxFileSize)
      {
        this.messageHandlerService.displayError('FILE-SIZE-EXCEED-LIMIT');
        return;
      }

      // Validate file extension
      let validExtension = false;
      this.fileRestrictions.allowedExtensions.forEach((ext: string) =>
      {
        if (file.name.toLowerCase().indexOf(ext) !== -1)
        {
          validExtension = true;
        }
      });
      if (!validExtension)
      {
        this.messageHandlerService.displayDirectMessage('error',
          this.invalidFileExtensionText.replace('{{value}}', this.fileRestrictions.allowedExtensions.join()));
        return;
      }

      const customFile = new CustomFile();
      customFile.name = file.name;
      customFile.type = file.rawFile.type;
      this.setFileFromBlob(customFile, file.rawFile);
    });
  }

  /**
   * Select attachment and open the uploader.
   */
  public selectJsonFile(): void
  {
    const uploadButton = this.elem.nativeElement.querySelectorAll('input[name="files"]');
    uploadButton[0].click();
  }

  public copyKeyToClipboard(label: string): void
  {
    copyToClipboard(label);
    this.messageHandlerService.displaySuccess('COPIED-TO-CLIPBOARD');
  }

  public onPatientModelNodeDblClick(event: MouseEvent): void
  {
    const root = this.patientModelViewer?.nativeElement;
    const path = resolveNgxJsonViewerPath(event.target as HTMLElement, root);
    if (!path)
    {
      return;
    }

    if (this.patientModelViewer?.nativeElement)
    {
      clearNgxJsonViewerPathSelection(this.patientModelViewer.nativeElement);
      highlightNgxJsonViewerPathSegment(event.target as HTMLElement);
    }

    this.selectedModelPath = path;
    this.copyKeyToClipboard(path);
  }

  public copyPatientModelPath(): void
  {
    if (this.selectedModelPath)
    {
      this.copyKeyToClipboard(this.selectedModelPath);
    }
  }

  private syncPreviewPatientFormState(): void
  {
    this.formlyPreviewOptions = {
      ...this.formlyPreviewOptions,
      formState: { patient: this.patientModel ?? null }
    };
  }

  private preparePreviewForm(): void
  {
    if (!this.fieldsPreview?.length)
    {
      return;
    }

    this.syncPreviewPatientFormState();

    const previewModel: Record<string, unknown> = {};
    this.assessmentService.setFormlyModelDefaults(this.fieldsPreview, previewModel);
    this.assessmentService.applyFormlyInterpolatedExpressions(
      this.fieldsPreview, previewModel, this.formlyPreviewOptions.formState);

    this.model = previewModel;
    this.form = new UntypedFormGroup({});
    this.changeDetectorRef.detectChanges();
    this.deferPreviewControlSync();
  }

  /**
   * Runs after the preview formly-form is in the DOM so controls receive evaluated defaults and sums.
   */
  private deferPreviewControlSync(): void
  {
    const sync = (): void =>
    {
      if (!this.fieldsPreview?.length)
      {
        return;
      }

      this.assessmentService.triggerFormlyExpressionRefresh(this.formlyPreviewOptions, this.fieldsPreview);
      this.assessmentService.applyModelValuesToFormControls(this.fieldsPreview, this.model);
      this.assessmentService.setFormlyControlsSum(this.fieldsPreview, this.form);
    };

    this.assessmentService.runAfterFormlyFieldsAttach(sync, 1);
  }

  /**
   * Key a list of used keys
   */
  private setUsedKeys(): void
  {
    this.usedKeys = [];
    this.addKeys(this.fields);
  }

  /**
   * Add used keys recursively.
   * @param fields Fields
   */
  private addKeys(fields: any): void
  {
    fields.forEach((x: any) => 
    {
      this.usedKeys.push(x.key);
      if (x.fieldGroup) 
      {
        this.addKeys(x.fieldGroup);
      }
    });
  }

  /**
   * Set the selected file.
   *
   * @param file Selected file.
   */
  private setFileFromBlob(customFile: CustomFile, file: Blob): void
  {
    const reader = new FileReader();
    reader.addEventListener('load', () =>
    {
      const base64: any = reader.result;
      customFile.data = base64.split('base64,')[1];
      const importedData = JSON.parse(Base64.decode(customFile.data));
      if (importedData.type && importedData.fields)
      {
        this.fields = importedData.fields;
        this.fieldsChanged.emit(this.fields);
      }
      else
      {
        this.messageHandlerService.displayError('IMPORT-INVALID-ASSESSMENT-FILE');
      }
    }, false);
    reader.readAsDataURL(file);
  }

  /**
   * Validate if field can be deleted.
   *
   * @param field Field definition
   */
  private canDeleteField(field: any): boolean
  {
    let canDelete = true;

    // If formly-field as a key defined (not a separator, header, etc)
    if (field.key !== undefined)
    {
      // Validate Flags Rules
      if (this.isFieldInUseByQueryRules(this.flags, field.key))
      {
        this.messageHandlerService.displayError('ASSESSMENT-FORM-FIELD-IN-USE-FLAG');
        canDelete = false;
      }

      // Validate Tasks Rules
      if (this.isFieldInUseByQueryRules(this.tasks, field.key))
      {
        this.messageHandlerService.displayError('ASSESSMENT-FORM-FIELD-IN-USE-TASK');
        canDelete = false;
      }

      // Validate Service Types Rules
      if (this.isFieldInUseByQueryRules(this.serviceTypes, field.key))
      {
        this.messageHandlerService.displayError('ASSESSMENT-FORM-FIELD-IN-USE-RISK');
        canDelete = false;
      }

      // Validate Channels Rules
      if (this.isFieldInUseByQueryRules(this.channels, field.key))
      {
        this.messageHandlerService.displayError('ASSESSMENT-FORM-FIELD-IN-USE-CHANNEL');
        canDelete = false;
      }

      // Validate Needs Rules
      if (this.isFieldInUseByQueryRules(this.needs, field.key))
      {
        this.messageHandlerService.displayError('ASSESSMENT-FORM-FIELD-IN-USE-NEED');
        canDelete = false;
      }
    }
    return canDelete;
  }

  /**
   * Check if the formly-field is used by a query-builder object.
   *
   * @param objectToValidate Object to validate.
   * @param validateKey Field name to validate.
   */
  private isFieldInUseByQueryRules(objectToValidate: any, fieldName: string): boolean
  {
    let inUse = false;

    objectToValidate.forEach(data =>
    {
      if (data.query && data.query.rules)
      {
        const obj = this.findRuleItemByKey(data.query.rules, fieldName);
        if (obj !== undefined)
        {
          inUse = true;
        }
      }
    });
    return inUse;
  }

  /**
   * Find recursively query rules that matches the fieldKey passed in.
   *
   * @param items Query object.
   * @param key Key to search.
   */
  private findRuleItemByKey(items: any, fieldKey: string): any
  {
    if (!items) { return; }
    for (const item of items)
    {
      // Test current object
      if (item.field === fieldKey) { return item; }

      // Test rules recursively
      const child = this.findRuleItemByKey(item.rules, fieldKey);
      if (child) { return child; }
    }
  }

  /**
   * Found field to update recursively
   * 
   * @param searchFieldKey Field key to search
   * @param field Field data
   * @param fields Fields data
   */
  private foundFieldAndUpdate(searchFieldKey: string, field: any, fields: any[], tabGroup: any): void
  {
    if (!this.foundFieldInArray)
    {
      let index = -1;
      fields.forEach(x =>
      {
        index += 1;
        if (x.fieldGroup)
        {
          this.foundFieldAndUpdate(searchFieldKey, field, x.fieldGroup, x);
        }
        else
        {
          // If field found. Must compare tab to see if field but be added to a new tab.
          if (x.key === searchFieldKey)
          {
            // Check if can update the field (tab selection must not have changed)
            if ((field.tab ? field.tab : null) === (tabGroup ? tabGroup.key : null))
            {
              x.templateOptions = field.templateOptions;

              if (field.hideExpression)
              {
                x.hideExpression = field.hideExpression;
              }
              else
              {
                delete x.hideExpression;
              }

              if (field.expressionProperties)
              {
                x.expressionProperties = field.expressionProperties;
              }
              else
              {
                delete x.expressionProperties;
              }

              if (field.wrappers)
              {
                x.wrappers = field.wrappers;
              }
              else
              {
                delete x.wrappers;
              }

              if (field.defaultValue !== undefined && field.defaultValue !== null &&
                (typeof field.defaultValue === 'boolean' || field.defaultValue !== ''))
              {
                x.defaultValue = field.defaultValue;
              }
              else
              {
                delete x.defaultValue;
              }

              if (field.template !== undefined)
              {
                x.template = field.template;
              }
              else
              {
                delete x.template;
              }

              if (field.validators)
              {
                x.validators = field.validators;
              }
              else
              {
                delete x.validators;
              }

              this.foundFieldInArray = true;
            }
            else
            {
              fields = fields.splice(index, 1);     // Delete property and will be re-added
            }
          }
        }
      });
    }
  }

  private foundFieldAndAdd(searchTabKey: string, field: any, fields: any[]): void
  {
    fields.forEach(x =>
    {
      if (x.type === 'tabs' && x.fieldGroup)
      {
        x.fieldGroup.forEach((tabGroup: any) =>
        {
          // Found the right tab
          if (tabGroup.key === searchTabKey)
          {
            // Create the array if this is the first field to add in the tab
            if (!tabGroup.fieldGroup) { tabGroup.fieldGroup = []; }
            tabGroup.fieldGroup.push(field);
          }
        });
      }
    });
  }

  /** Recursively sorts object keys A–Z for ngx-json-viewer (arrays keep order; elements are sorted). */
  private sortPatientModelKeysAlphabetically(value: any): any
  {
    if (value === null || value === undefined)
    {
      return value;
    }

    if (Array.isArray(value))
    {
      return value.map((item) => this.sortPatientModelKeysAlphabetically(item));
    }

    if (typeof value === 'object')
    {
      const sorted: Record<string, unknown> = {};
      Object.keys(value)
        .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
        .forEach((key) =>
        {
          sorted[key] = this.sortPatientModelKeysAlphabetically(value[key]);
        });
      return sorted;
    }

    return value;
  }

  /** Truncates `photo` string values for JSON viewer display; does not affect preview formState. */
  private truncatePhotoForViewerDisplay(value: any): any
  {
    if (value === null || value === undefined)
    {
      return value;
    }

    if (Array.isArray(value))
    {
      return value.map((item) => this.truncatePhotoForViewerDisplay(item));
    }

    if (typeof value === 'object')
    {
      const result: Record<string, unknown> = {};
      Object.keys(value).forEach((key) =>
      {
        const child = value[key];
        result[key] = key === 'photo' && typeof child === 'string' && child.length > 15
          ? `${child.slice(0, 15)}...`
          : this.truncatePhotoForViewerDisplay(child);
      });
      return result;
    }

    return value;
  }

  private applyPatientContext(member: any): void
  {
    this.patientModel = member ? this.preparePatientModelForDesigner(member) : null;
    this.patientModelForViewer = this.patientModel
      ? this.truncatePhotoForViewerDisplay(cloneDeep(this.patientModel))
      : null;
    this.selectedModelPath = null;
    this.syncPreviewPatientFormState();
    this.persistContextMemberId(member?.id ?? null);

    if (member)
    {
      const selection = this.toPatientSearchSelection(member);
      this.selectedPatientSearch = selection;
      this.patientSearchItems = [selection];
    }
    else
    {
      this.selectedPatientSearch = null;
    }

    if (this.fieldsPreview?.length)
    {
      this.preparePreviewForm();
    }

    this.changeDetectorRef.markForCheck();
  }

  private tryRestoreStoredContextPatient(): void
  {
    if (this.contextPatientRestoreStarted || this.patientModel || !this.fields?.length
      || this.isDeleted || this.isLocked || !this.patientContextSearchEnabled)
    {
      return;
    }

    const storedId = Number(this.storageService.retrieve(FormlyDesignerComponent.CONTEXT_MEMBER_STORAGE_KEY));
    if (!storedId || Number.isNaN(storedId))
    {
      return;
    }

    this.contextPatientRestoreStarted = true;
    this.loadingPatientModel = true;
    this.memberService.getMember(storedId).subscribe({
      next: (member) =>
      {
        this.applyPatientContext(member);
        this.loadingPatientModel = false;
        this.changeDetectorRef.markForCheck();
      },
      error: (error) =>
      {
        this.storageService.clear(FormlyDesignerComponent.CONTEXT_MEMBER_STORAGE_KEY);
        this.loadingPatientModel = false;
        this.messageHandlerService.displayError(error);
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  private persistContextMemberId(memberId: number | null): void
  {
    const key = FormlyDesignerComponent.CONTEXT_MEMBER_STORAGE_KEY;
    if (memberId)
    {
      this.storageService.store(key, memberId);
    }
    else
    {
      this.storageService.clear(key);
    }
  }

  /** Shape aligned with member grid rows so the multicolumn combobox can display the selection. */
  private toPatientSearchSelection(member: any): Record<string, unknown>
  {
    const name = member?.name;
    const address = member?.address;
    const fullName = member?.fullName
      ?? name?.fullName
      ?? [name?.givenName ?? member?.givenName, name?.familyName ?? member?.familyName].filter(Boolean).join(' ').trim();

    return {
      id: member.id,
      fullName: fullName || String(member.id),
      dateOfBirth: member?.dateOfBirth,
      streetLine: member?.streetLine ?? address?.streetLine,
      streetLineAdditional: member?.streetLineAdditional ?? address?.streetLineAdditional,
      city: member?.city ?? address?.city,
      county: member?.county ?? address?.county,
      stateCode: member?.stateCode ?? address?.state?.code,
      postalCode: member?.postalCode ?? address?.postalCode
    };
  }

  private preparePatientModelForDesigner(member: any): Record<string, unknown>
  {
    const model = cloneDeep(member);
    delete model.memberOrganizations;
    delete model.memberMergeHistory;
    delete model.memberLink;
    delete model.starred;
    delete model.server;
    delete model.npi;
    return this.sortPatientModelKeysAlphabetically(model);
  }

}
